import React from 'react';
import { Box, Avatar, Typography, Chip, Badge } from '@mui/material';
import { CheckCircle as WinIcon } from '@mui/icons-material';
import { C, tEntity } from './constants';
import { initials } from './utils';

/**
 * EntityHeader — top banner with logo, name, location, live badge.
 *
 * Props:
 *   entity       — the entity object
 *   liveGames    — number of live games (shows pulsing badge if > 0)
 *   activeEvents — number of active/open events
 */
const EntityHeader = ({ entity, liveGames, activeEvents }) => (
  <Box sx={{
    background: `linear-gradient(135deg, ${C.accentSoft}60 0%, ${C.bg} 70%)`,
    borderBottom: `1px solid ${C.border}`,
    px: { xs: 2, md: 5 }, py: 4,
    animation: 'scaleIn 0.45s ease both',
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>

      {/* Logo */}
      <Badge
        overlap="circular"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        badgeContent={
          entity?.isVerified
            ? <Box sx={{ width: 14, height: 14, borderRadius: '50%', background: C.emerald, border: `2px solid ${C.bg}` }} />
            : null
        }
      >
        <Avatar
          src={entity?.logo}
          sx={{
            width: 64, height: 64,
            background: `linear-gradient(135deg, ${C.accentSoft}, ${C.accent}40)`,
            border: `2px solid ${C.accent}50`,
            fontFamily: 'Syne', fontWeight: 800, fontSize: '1.4rem', color: C.accent,
          }}
        >
          {initials(entity?.name)}
        </Avatar>
      </Badge>

      {/* Name + meta */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography sx={{
            fontFamily: 'Syne', fontWeight: 800,
            fontSize: { xs: '1.4rem', md: '1.9rem' }, color: C.text, lineHeight: 1,
          }}>
            {entity?.name}
          </Typography>
          {entity?.isVerified && (
            <Chip
              label="Vérifié" size="small"
              icon={<WinIcon sx={{ fontSize: '12px !important' }} />}
              sx={{
                background: `${C.emerald}18`, border: `1px solid ${C.emerald}40`,
                color: C.emerald, fontFamily: 'DM Sans', fontSize: '0.7rem',
              }}
            />
          )}
        </Box>
        <Typography sx={{ color: C.muted, fontFamily: 'DM Sans', fontSize: '0.85rem', mt: 0.4 }}>
          {[tEntity(entity?.type), entity?.city, entity?.country].filter(Boolean).join(' · ')}
          {entity?.foundedYear && ` · Fondé en ${entity.foundedYear}`}
        </Typography>
      </Box>

      {/* Badges */}
      <Box sx={{ ml: 'auto', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {liveGames > 0 && (
          <Chip
            label={`${liveGames} EN DIRECT`} size="small"
            sx={{
              background: `${C.rose}20`, border: `1px solid ${C.rose}50`, color: C.rose,
              fontFamily: 'Syne', fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.08em',
              animation: 'pulseRose 1.5s ease infinite',
            }}
          />
        )}
        {activeEvents > 0 && (
          <Chip
            label={`${activeEvents} Évènement${activeEvents > 1 ? 's' : ''} actif${activeEvents > 1 ? 's' : ''}`}
            size="small"
            sx={{
              background: `${C.amber}18`, border: `1px solid ${C.amber}40`,
              color: C.amber, fontFamily: 'DM Sans', fontSize: '0.75rem',
            }}
          />
        )}
      </Box>
    </Box>
  </Box>
);

export default EntityHeader;
